import { cn } from "@newxsystems/shared";
import { RavioliAction } from "../RavioliAction/RavioliAction";
import { RavioliMedia } from "../RavioliMedia/RavioliMedia";
import { RavioliRichText } from "../RavioliRichText/RavioliRichText";
import type { ICallToActionSplitProps } from "./CallToActionSplit.props";

const splitColumns = {
  small: "lg:grid-cols-[7fr_3fr]",
  medium: "lg:grid-cols-[11fr_9fr]",
  large: "lg:grid-cols-[2fr_3fr]",
} as const;

export const CallToActionSplit = ({
  buttons,
  description,
  eyebrow,
  heading,
  image,
  imageWidth,
}: ICallToActionSplitProps) => {
  const hasText = Boolean(eyebrow || heading || description);
  const hasButtons = Boolean(buttons?.length);
  const hasTwoRegions = Boolean(
    (image && (hasText || hasButtons)) || (!image && hasText && hasButtons),
  );

  return (
    <div
      className={cn(
        "grid grid-cols-1 items-center gap-7 md:gap-12",
        hasTwoRegions && (image ? splitColumns[imageWidth || "medium"] : "lg:grid-cols-2"),
      )}
    >
      {eyebrow ? (
        <p className="order-1 inline-flex w-fit items-center gap-2 border border-canvas/60 px-3 py-1.5 font-ravioli-body text-[0.8125rem] font-semibold before:block before:size-1.5 before:bg-canvas lg:col-start-1">
          {eyebrow}
        </p>
      ) : null}
      {image ? (
        <RavioliMedia
          aspectRatio={image.aspectRatio || "16:9"}
          className={cn(
            "order-2 justify-self-end border-4 border-canvas",
            "w-full",
            hasTwoRegions && "lg:col-start-2 lg:row-span-4 lg:row-start-1",
          )}
          media={image.image}
          sizes={hasTwoRegions ? "(min-width: 1024px) 50vw, 100vw" : "100vw"}
        />
      ) : null}
      {heading ? (
        <h2 className="order-3 w-full font-ravioli-display text-5xl font-semibold leading-[0.96] tracking-[-0.02em] text-balance [overflow-wrap:break-word] lg:col-start-1 md:text-7xl">
          {heading}
        </h2>
      ) : null}
      {description ? (
        <RavioliRichText
          className="order-4 max-w-[65ch] text-canvas lg:col-start-1"
          content={description}
        />
      ) : null}
      {buttons?.length ? (
        <div
          className={cn(
            "order-5 flex flex-wrap gap-3",
            image && hasTwoRegions && "lg:col-start-1",
            !image && hasText && "lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:justify-self-end",
          )}
        >
          {buttons.map((button, index) => (
            <RavioliAction
              action={button}
              className="border-canvas bg-canvas text-primary"
              key={`${button.href}-${index}`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

CallToActionSplit.displayName = "Content::CallToActionSplit";
